import { z } from "zod";

export const taskComplexitySchema = z.enum(["standard", "complex"]);
export const taskDesignFreedomSchema = z.enum(["strict", "guided", "open"]);
export const taskPlanStepStatusSchema = z.enum(["pending", "in_progress", "completed", "blocked"]);

/**
 * Cross-cutting professional method required whenever meX owns a visual or
 * spatial choice. Domain skills (joinery, kitchens, architecture, etc.) remain
 * complementary; this skill is the invariant quality layer.
 */
export const VISUAL_COMPOSITION_SKILL_SLUG = "compor-layout-com-qualidade-visual";

/**
 * Linha de referência de uma cota vertical. Um projetista não cota um suspenso
 * "por folga sobre o vizinho": ele escolhe uma linha — a que a parede já tem,
 * o teto, a verga, a faixa de uso da atividade — ou abre uma linha nova e diz
 * por quê. O datum torna essa escolha auditável e dá ao crítico a mesma
 * referência para conferir.
 */
export const taskVerticalDatumSchema = z.object({
  kind: z.enum(["existing-line", "ceiling", "opening-head", "use-band", "reference-top", "new-line"])
    .describe("existing-line: topo ou base alinhados a uma linha que a parede já tem (outro suspenso, topo de torre, verga); ceiling: topo no teto; opening-head: alinhado à verga; use-band: dentro da faixa de uso da atividade na superfície de baixo; reference-top: apoio/empilhamento declarado; new-line: abre uma linha nova na parede (exige justificativa)."),
  reference: z.string().trim().min(1).max(160).optional()
    .describe("Elemento, parede ou vão cuja linha foi adotada (nome usado na leitura do ambiente)."),
  justification: z.string().trim().min(1).max(300).optional()
    .describe("Obrigatória para new-line: por que a composição abre uma linha nova em vez de seguir as existentes."),
});

export const taskSpatialDecisionSchema = z.object({
  subject: z.string().trim().min(1).max(160)
    .describe("Elemento ou conjunto cuja implantação está sendo decidida."),
  subjectElementId: z.string().trim().min(1).max(200).optional()
    .describe("Quando o sujeito JÁ existe na cena, o id do elemento (de scene.read/environment.read). O vínculo por id garante crédito de execução e verificação da entrega; nome sozinho não identifica variantes (claro/escuro) nem apelidos."),
  relation: z.enum(["above", "below", "beside", "aligned", "centered", "inside", "against", "stacked", "custom"])
    .describe("Relação espacial profissional pretendida, não uma interpretação literal da preposição usada no briefing."),
  reference: z.string().trim().min(1).max(160)
    .describe("Parede, móvel, vão ou conjunto usado como referência."),
  referenceElementId: z.string().trim().min(1).max(200).optional()
    .describe("Identificador real do elemento de referência quando ele já existe na cena (obtido por scene.read ou scene.environment.read). Torna a referência inequívoca para o validador, as ferramentas e o revisor."),
  support: z.enum(["floor", "wall", "ceiling", "element", "unconstrained"])
    .describe("Superfície que realmente sustenta ou fixa o elemento."),
  clearanceMm: z.object({
    minimum: z.number().min(0).max(100_000),
    target: z.number().min(0).max(100_000),
    maximum: z.number().min(0).max(100_000),
  }).superRefine((clearance, context) => {
    if (clearance.minimum > clearance.target || clearance.target > clearance.maximum) {
      context.addIssue({ code: "custom", message: "A folga-alvo precisa ficar entre a mínima e a máxima." });
    }
  }).nullable().default(null)
    .describe("Faixa de folga funcional escolhida. Use null quando a relação não depender de distância."),
  allowContact: z.boolean().default(false)
    .describe("Contato físico intencional. Só use quando apoio, catálogo, construção ou uma restrição explícita realmente o exigirem."),
  bottomHeightMm: z.number().min(0).max(100_000).nullable().optional()
    .describe("Cota absoluta da face inferior do sujeito em relação ao piso, em milímetros. Obrigatória (ou topHeightMm) em toda relação vertical de um elemento suspenso: é a decisão de projeto; a folga sobre a referência é consequência."),
  topHeightMm: z.number().min(0).max(100_000).nullable().optional()
    .describe("Cota absoluta do topo do sujeito em relação ao piso, quando a decisão for alinhar o topo (teto, verga, topo de outro elemento)."),
  datum: taskVerticalDatumSchema.nullable().optional()
    .describe("Linha de referência da cota vertical: a que a parede já tem, o teto, a verga, a faixa de uso ou uma linha nova justificada. Obrigatória quando bottomHeightMm/topHeightMm forem informados."),
  source: z.enum(["user", "reference", "catalog", "scene", "skill", "inferred"])
    .describe("Origem da decisão: briefing, referência, catálogo, método profissional ou inferência contextual."),
  rationale: z.string().trim().min(10).max(360)
    .describe("Justificativa funcional, construtiva e visual para a relação escolhida."),
}).superRefine((decision, context) => {
  if ((decision.bottomHeightMm != null || decision.topHeightMm != null) && !decision.datum) {
    context.addIssue({ code: "custom", path: ["datum"], message: "Uma cota vertical absoluta precisa declarar a linha de referência (datum)." });
  }
  if (decision.datum?.kind === "new-line" && !(decision.datum.justification ?? "").trim()) {
    context.addIssue({ code: "custom", path: ["datum", "justification"], message: "Uma linha nova precisa de justificativa de composição." });
  }
});

/**
 * Premissa declarada: o que o plano assumiu sobre o cômodo, os usuários, a
 * atividade na superfície ou o alcance quando a cena não prova. Premissas
 * aparecem na resposta final para o projetista confirmar ou corrigir — é o
 * padrão; perguntar é a exceção.
 */
export const taskAssumptionSchema = z.object({
  topic: z.enum(["room-function", "users", "activity", "reach", "operation", "other"])
    .describe("Assunto da premissa: função do cômodo, perfil dos usuários, atividade na superfície, alcance/guarda, operação de partes móveis ou outro."),
  assumption: z.string().trim().min(5).max(300)
    .describe("O que foi assumido, em uma frase verificável."),
  basis: z.string().trim().min(3).max(300)
    .describe("Evidência ou raciocínio que sustenta a premissa (mobiliário presente, leitura do ambiente, briefing)."),
  subject: z.string().trim().min(1).max(160).optional()
    .describe("Elemento ou superfície da cena a que a premissa se refere, com o nome usado na cena."),
  surfaceActivity: z.enum(["work_standing", "work_seated", "support", "display", "seating", "sleeping", "appliance", "circulation", "unknown"]).optional()
    .describe("Quando a premissa declara a atividade real da superfície de referência, registre-a aqui: é a declaração que seleciona a faixa da régua (não a inferência da leitura), e ela fica sujeita à revisão de prancheta."),
});

/** Parecer do revisor independente de prancheta sobre o plano proposto, antes da execução. */
export const planDesignReviewInputSchema = z.object({
  verdict: z.enum(["sign", "object"])
    .describe("sign: você assinaria esta prancheta (ressalvas viram observations). object: existe UMA objeção principal que mudaria a obra."),
  summary: z.string().trim().min(10).max(600)
    .describe("Parecer de colega sênior em 1–3 frases, citando cotas e elementos das leituras."),
  observations: z.array(z.string().trim().min(5).max(300)).max(4).default([])
    .describe("Ressalvas que não impedem a assinatura; ficam registradas, não bloqueiam."),
  objection: z.object({
    subject: z.string().trim().min(1).max(160)
      .describe("O sujeito da decisão objetada, como escrito no plano."),
    grounding: z.string().trim().min(10).max(500)
      .describe("A objeção ancorada nas leituras: cite a cota em mm, a linha da parede, o elemento ou a premissa que a sustenta. Objeção sem âncora é rebaixada a observação."),
    request: z.string().trim().min(10).max(400)
      .describe("O ajuste acionável: nova cota com datum, outra referência ou dimensão, ou a premissa a declarar."),
  }).optional(),
}).superRefine((value, ctx) => {
  if (value.verdict === "object" && !value.objection) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["objection"], message: "verdict=object exige a objeção principal." });
  }
});

export type PlanDesignReviewInput = z.infer<typeof planDesignReviewInputSchema>;

export const taskSkillCoverageSchema = z.object({
  skillSlug: z.string().trim().min(3).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .describe("Skill profissional mais específica para esta decisão."),
  coversFreeVariables: z.array(z.string().trim().min(1).max(240)).min(1).max(12)
    .describe("Variáveis livres do plano cobertas por esta skill; copie o texto exato usado em freeVariables."),
  reason: z.string().trim().min(10).max(300)
    .describe("Por que este método é adequado para tomar essas decisões."),
});

export const taskDecisionAuthoritySchema = z.object({
  subject: z.string().trim().min(1).max(160)
    .describe("Elemento ou conjunto ao qual a decisão se aplica."),
  attribute: z.enum(["position", "dimensions", "orientation", "material", "product", "quantity", "relation", "function", "appearance"])
    .describe("Aspecto que a revisão final pode avaliar."),
  authority: z.enum(["explicit", "agent"])
    .describe("explicit protege uma definição do briefing ou referência; agent permite corrigir uma escolha feita pelo agente."),
  source: z.enum(["user", "reference", "catalog", "scene", "skill", "inferred"])
    .describe("Origem comprovável: briefing, referência, catálogo, estado da cena, método profissional ou inferência contextual."),
  requirement: z.string().trim().min(3).max(360)
    .describe("Definição preservada ou critério usado para a escolha do agente."),
});

const taskDesignAttributeSchema = z.enum([
  "position",
  "dimensions",
  "orientation",
  "material",
  "product",
  "quantity",
  "relation",
  "function",
  "appearance",
]);

const taskTargetDimensionsSchema = z.object({
  width: z.number().positive().max(100_000).optional(),
  height: z.number().positive().max(100_000).optional(),
  depth: z.number().positive().max(100_000).optional(),
}).refine((dimensions) => Object.values(dimensions).some((value) => value !== undefined), {
  message: "Informe ao menos uma dimensão-alvo.",
});

export const taskDesignReviewCriterionSchema = z.object({
  aspect: z.enum([
    "proportion",
    "scale",
    "alignment",
    "spacing",
    "rhythm",
    "coherence",
    "material",
    "function",
    "installation",
    "brief_fidelity",
    "use_height",
    "operation",
    "datum_alignment",
  ]).describe("Aspecto profissional que precisa ser julgado depois da execução. use_height: cota de uso e alcance do conteúdo; operation: envelope de portas, folhas e gavetas abertas; datum_alignment: linhas da parede (topos, bases, teto, vergas) que o elemento segue ou quebra."),
  requirement: z.string().trim().min(10).max(300)
    .describe("Condição concreta que diferencia uma solução profissional de uma implantação apenas geometricamente válida."),
  evidence: z.enum(["visual", "geometric", "structured", "catalog"])
    .describe("Tipo de evidência necessário para avaliar o critério sem inventar fatos."),
  referenceElementId: z.string().trim().min(1).max(200).optional()
    .describe("Elemento real usado na comparação de proporção, alinhamento, escala, material ou relação, quando aplicável."),
});

/**
 * Turns professional authorship into an executable choice made before the
 * first mutation. Structured targets let the runtime verify catalog selection
 * and achieved dimensions without pretending that prose is geometry.
 */
export const taskDesignResolutionSchema = z.object({
  stepId: z.string().trim().min(1).max(40)
    .describe("Etapa que executará esta decisão."),
  subject: z.string().trim().min(1).max(160)
    .describe("Elemento ou conjunto ao qual a decisão se aplica."),
  subjectElementId: z.string().trim().min(1).max(200).optional()
    .describe("Id do elemento quando o sujeito já existe na cena; vincula a resolução ao alvo sem depender do nome."),
  instanceKey: z.string().trim().min(1).max(120).regex(/^[a-z0-9][a-z0-9_-]*$/i).optional()
    .describe("Chave da unidade quando a etapa criar mais de uma instância semelhante."),
  attribute: taskDesignAttributeSchema,
  authority: z.enum(["explicit", "derived", "agent"])
    .describe("Quem decidiu este atributo: explicit quando o briefing/referência fixou o resultado; derived quando a coordenada ou valor apenas materializa uma diretriz explícita sem redesenho; agent somente quando o meX realmente escolheu entre soluções profissionais plausíveis. A altura, folga ou implantação de um elemento novo e qualquer valor que resolva uma variável livre são sempre agent."),
  authorityNormalized: z.enum(["resolves-free-variable", "position-of-new-element"]).optional()
    .describe("Preenchido pelo CalcPlan quando uma resolução derived foi reclassificada como agent por resolver variável livre ou definir a implantação de um elemento novo. Não preencha manualmente."),
  resolvesFreeVariables: z.array(z.string().trim().min(1).max(240)).max(12).default([])
    .describe("Variáveis livres efetivamente resolvidas; copie o texto de freeVariables. Use [] em uma resolução explicit ou derived, pois ela apenas preserva/materializa uma diretriz externa e não cria liberdade de projeto."),
  decision: z.string().trim().min(5).max(360)
    .describe("Escolha concreta e executável já tomada, nunca algo 'a definir' durante a execução."),
  source: z.enum(["user", "reference", "catalog", "scene", "skill", "inferred"])
    .describe("Evidência que sustenta a escolha profissional: briefing, referência, catálogo, cena, skill ou inferência."),
  targetCatalogItemId: z.string().trim().min(1).max(200).optional()
    .describe("Obrigatório para uma escolha livre de produto de catálogo."),
  targetDimensionsMm: taskTargetDimensionsSchema.optional()
    .describe("Obrigatório para dimensões deixadas à autoria do agente. Representa a medida nominal/paramétrica gravada no elemento; o volume físico renderizado é conferido separadamente por instalação, folgas e revisão visual."),
  targetMaterialCatalogItemId: z.string().trim().min(1).max(200).optional()
    .describe("Material exato já escolhido no catálogo quando a resolução decide cor, material ou acabamento."),
  targetReferenceElementId: z.string().trim().min(1).max(200).optional()
    .describe("Elemento real cuja aparência deve ser reproduzida quando a decisão for 'mesma cor/material/acabamento'."),
  reviewCriteria: z.array(taskDesignReviewCriterionSchema).max(8).default([])
    .describe("Critérios profissionais observáveis que o executor e o revisor independente devem testar. Decisões autorais visuais não podem depender apenas de 'parece bom'."),
  rationale: z.string().trim().min(10).max(360)
    .describe("Por que a escolha atende função, contexto e composição."),
  verification: z.string().trim().min(5).max(240)
    .describe("Condição observável usada para confirmar que a decisão foi aplicada."),
});

export const taskDesignAlternativeSchema = z.object({
  label: z.string().trim().min(1).max(80),
  solution: z.string().trim().min(10).max(400),
  strengths: z.array(z.string().trim().min(1).max(180)).min(1).max(6),
  risks: z.array(z.string().trim().min(1).max(180)).max(6),
});

export const taskPlanStepProposalSchema = z.object({
  id: z.string().trim().min(1).max(40)
    .regex(/^[a-z0-9][a-z0-9_-]*$/i)
    .describe("Identificador curto e estável desta entrega."),
  title: z.string().trim().min(1).max(120)
    .describe("Nome concreto da entrega, compreensível para o projetista."),
  deliverable: z.string().trim().min(1).max(400)
    .describe("Resultado observável que deve existir ao concluir a etapa."),
  dependsOn: z.array(z.string().trim().min(1).max(40)).max(8).default([]),
  acceptanceCriteria: z.array(z.string().trim().min(1).max(240)).min(1).max(8)
    .describe("Condições verificáveis para considerar a etapa concluída."),
  evidence: z.array(z.enum(["readback", "clearance", "grounding", "catalog", "visual", "server_result"]))
    .min(1).max(6)
    .describe("Evidências que precisam ser obtidas, sem inventar dados."),
});

export const taskPlanProposalSchema = z.object({
  complexity: taskComplexitySchema.describe(
    "Complexidade de execução por alcance, quantidade de etapas e risco. É independente da liberdade de projeto.",
  ),
  designFreedom: taskDesignFreedomSchema.describe(
    "Liberdade de projeto determinada pelo grau de especificação do briefing, nunca pelo tamanho da tarefa: strict reproduz o que foi definido; guided preserva as definições e decide apenas as lacunas; open recebe autoria ampla dentro do escopo.",
  ),
  summary: z.string().trim().min(1).max(360)
    .describe("Síntese do que será entregue, interpretando a intenção em vez de repetir literalmente o pedido."),
  userIntent: z.string().trim().min(1).max(800)
    .describe("Objetivo funcional, espacial e estético que existe por trás do pedido."),
  scope: z.string().trim().min(1).max(500)
    .describe("Alvos que podem ser alterados e limites explícitos do trabalho."),
  hardConstraints: z.array(z.string().trim().min(1).max(240)).max(20).default([]),
  creativeCriteria: z.array(z.string().trim().min(1).max(240)).max(12).default([])
    .describe("Critérios profissionais para resolver variáveis livres com autoria."),
  freeVariables: z.array(z.string().trim().min(1).max(240)).max(12).default([]),
  spatialDecisions: z.array(taskSpatialDecisionSchema).max(16).default([])
    .describe("Contrato espacial das posições deixadas em aberto: referência, suporte, folga e intenção de contato."),
  decisionAuthorities: z.array(taskDecisionAuthoritySchema).max(40).default([])
    .describe("Metadado de proteção da revisão. Pode ser omitido: o runtime o deriva de designResolutions; use-o apenas para registrar proteção adicional fora dessas resoluções."),
  designResolutions: z.array(taskDesignResolutionSchema).max(40)
    .describe("Campo obrigatório. Para autoria strict sem lacunas use []; para cada variável livre, registre antes da mutação as escolhas concretas necessárias. Uma resolução de produto pode carregar também targetDimensionsMm quando produto e dimensões forem decididos juntos."),
  skillCoverage: z.array(taskSkillCoverageSchema).max(8).default([])
    .describe("Rastreabilidade do método profissional. O runtime normaliza diferenças de redação; informe as skills específicas quando houver mais de uma especialidade."),
  alternativesConsidered: z.array(taskDesignAlternativeSchema).max(4).default([])
    .describe("Soluções visualmente distintas comparadas antes de qualquer escolha autoral; em autoria guiada podem ser variações locais e objetivas."),
  selectedAlternative: z.string().trim().min(1).max(80).nullable().default(null)
    .describe("Label da alternativa escolhida depois de comparar função, geometria e qualidade visual."),
  discoverySummary: z.array(z.string().trim().min(1).max(300)).min(1).max(16)
    .describe("Fatos relevantes confirmados na cena, no catálogo ou no servidor durante a descoberta."),
  assumptions: z.array(taskAssumptionSchema).max(8).optional()
    .describe("Premissas declaradas sobre função do cômodo, usuários, atividade, alcance ou operação quando a cena não prova; são repetidas na resposta final."),
  actionCoverage: z.array(z.object({
    actionId: z.string().trim().min(1).max(80)
      .describe("Id literal recebido no objetivo no formato [ação:id]."),
    stepIds: z.array(z.string().trim().min(1).max(40)).min(1).max(16)
      .describe("Etapas deste mesmo plano que entregam integralmente a ação."),
  })).max(12).optional()
    .describe("Cobertura obrigatória quando o objetivo contém [ação:id]. Todas as ações da mensagem pertencem a este único plano; nenhuma pode ser adiada para outro planejamento."),
  steps: z.array(taskPlanStepProposalSchema).min(1).max(16),
  requiresApproval: z.boolean().default(false)
    .describe("Campo legado. O plano é um controle interno e não exige aprovação; impedimentos materiais usam uma pergunta específica durante a execução."),
});

export const taskPlanStepSchema = taskPlanStepProposalSchema.extend({
  status: taskPlanStepStatusSchema,
  note: z.string().trim().max(400).optional(),
  confirmedEvidence: z.array(z.string().trim().min(1).max(240)).max(10).default([]),
});

export const taskPlanSchema = taskPlanProposalSchema.extend({
  id: z.string().min(1).max(100),
  revision: z.number().int().min(1).max(100),
  steps: z.array(taskPlanStepSchema).min(1).max(16),
});

export const taskPlanProgressInputSchema = z.object({
  stepId: z.string().trim().min(1).max(40),
  status: z.enum(["in_progress", "completed", "blocked"]),
  note: z.string().trim().min(1).max(400)
    .describe("Resumo factual do avanço, impedimento ou decisão tomada."),
  evidence: z.array(z.string().trim().min(1).max(240)).max(10).default([])
    .describe("Evidências realmente observadas. Obrigatórias para concluir a etapa."),
  question: z.string().trim().min(1).max(500).optional()
    .describe("Pergunta específica ao projetista quando a etapa ficar bloqueada."),
}).superRefine((input, context) => {
  if (input.status === "completed" && input.evidence.length === 0) {
    context.addIssue({ code: "custom", path: ["evidence"], message: "Uma etapa concluída precisa de evidência observada." });
  }
  if (input.status === "blocked" && !input.question) {
    context.addIssue({ code: "custom", path: ["question"], message: "Uma etapa bloqueada precisa de uma pergunta específica." });
  }
});

/**
 * A plan is a control document, not scripture. When execution proves that a
 * spatial contract cannot be met — or the professional review shows it was
 * never professional (an upper cabinet resting on the counter) — the executor
 * must be able to amend that one decision, with evidence, instead of choosing
 * between disobeying the plan and looping forever. The amendment is audited
 * and re-validated against the same coherence rules as the original plan.
 */
export const taskPlanAmendmentInputSchema = z.object({
  subjectElementId: z.string().trim().min(1).max(200).optional()
    .describe("Id do elemento do sujeito, quando existir na cena — vincula a decisão (emendada ou registrada) ao alvo sem ambiguidade de nome."),
  decisionIndex: z.number().int().min(1).max(16).optional()
    .describe("Índice 1-based da decisão em spatialDecisions, para emendar sem ambiguidade quando o mesmo subject aparece em mais de uma decisão. A mensagem de erro de ambiguidade lista os índices."),
  subject: z.string().trim().min(1).max(160)
    .describe("Sujeito da spatialDecision a emendar, com o mesmo texto usado no plano."),
  reference: z.string().trim().min(1).max(160).optional()
    .describe("Nova referência, somente quando ela mudar."),
  referenceElementId: z.string().trim().min(1).max(200).optional()
    .describe("Identificador real da nova referência, quando ela mudar."),
  relation: taskSpatialDecisionSchema.shape.relation.optional()
    .describe("Nova relação, somente quando ela mudar. Não pode contrariar uma relação explicitamente pedida pelo projetista."),
  support: taskSpatialDecisionSchema.shape.support.optional()
    .describe("Novo suporte real do elemento, quando mudar."),
  clearanceMm: z.object({
    minimum: z.number().min(0).max(100_000),
    target: z.number().min(0).max(100_000),
    maximum: z.number().min(0).max(100_000),
  }).superRefine((clearance, context) => {
    if (clearance.minimum > clearance.target || clearance.target > clearance.maximum) {
      context.addIssue({ code: "custom", message: "A folga-alvo precisa ficar entre a mínima e a máxima." });
    }
  }).nullable().optional()
    .describe("Nova faixa de folga funcional em milímetros; null quando a relação não depender de distância."),
  allowContact: z.boolean().optional()
    .describe("Novo valor da intenção de contato físico."),
  bottomHeightMm: z.number().min(0).max(100_000).nullable().optional()
    .describe("Nova cota absoluta da face inferior (mm do piso), quando a emenda mudar a altura."),
  topHeightMm: z.number().min(0).max(100_000).nullable().optional()
    .describe("Nova cota absoluta do topo, quando a emenda alinhar o topo."),
  datum: taskVerticalDatumSchema.nullable().optional()
    .describe("Nova linha de referência da cota vertical (obrigatória quando a cota mudar)."),
  reason: z.string().trim().min(10).max(400)
    .describe("Por que o contrato anterior não pode ser executado ou não é profissional. Cite a evidência: leitura, laudo visual, conferência de instalação ou regra de tipologia."),
  evidence: z.array(z.string().trim().min(1).max(240)).min(1).max(8)
    .describe("Evidências observadas que sustentam a emenda."),
});

export type TaskPlanAmendmentInput = z.infer<typeof taskPlanAmendmentInputSchema>;

export interface TaskPlanAmendmentResult {
  plan: TaskPlan;
  previous: TaskSpatialDecision;
  amended: TaskSpatialDecision;
  changedFields: string[];
}

export function planSubjectsRefer(left: string, right: string): boolean {
  const ignored = new Set(["novo", "nova", "existente", "movido", "movida", "modulo", "movel", "elemento", "conjunto", "reposicionado", "reposicionada"]);
  const terms = (value: string): string[] => value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .split(/[^a-z0-9]+/)
    .filter((term) => term.length >= 3 && !ignored.has(term));
  const a = terms(left);
  const b = new Set(terms(right));
  if (!a.length || !b.size) return normalizeSubject(left) === normalizeSubject(right);
  return a.some((term) => b.has(term));
}

function normalizeSubject(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").replace(/\s+/g, " ").trim();
}

/**
 * Applies an amendment to exactly one spatial decision. Throws when the
 * subject does not identify a single decision or when nothing changes, so the
 * caller can return a precise error to the model instead of a silent no-op.
 */
export function amendTaskPlanSpatialDecision(plan: TaskPlan, rawInput: TaskPlanAmendmentInput): TaskPlanAmendmentResult {
  const input = taskPlanAmendmentInputSchema.parse(rawInput);
  if (input.decisionIndex != null) {
    const byIndex = plan.spatialDecisions[input.decisionIndex - 1];
    if (!byIndex) {
      throw new Error(`decisionIndex ${input.decisionIndex} fora do plano: há ${plan.spatialDecisions.length} decisões espaciais.`);
    }
  }
  const exact = plan.spatialDecisions.filter((decision) => normalizeSubject(decision.subject) === normalizeSubject(input.subject));
  let matches = input.decisionIndex != null
    ? [plan.spatialDecisions[input.decisionIndex - 1]!]
    : (exact.length ? exact : plan.spatialDecisions.filter((decision) => planSubjectsRefer(decision.subject, input.subject)));
  if (matches.length === 0) {
    const registered = plan.spatialDecisions
      .map((decision, index) => `${index + 1}) “${decision.subject}” ${decision.relation}→${decision.reference}`)
      .join("; ");
    // O laudo pode exigir emenda de um contrato que o plano nunca registrou
    // (20/08 13:38 — plano com uma única decisão; o aéreo não tinha o que
    // emendar e o turno morreu batendo nesta parede). Uma emenda completa
    // REGISTRA a decisão que falta: o executor formaliza a própria decisão
    // criativa, o contrato passa a existir e as réguas passam a conferi-lo.
    if (input.relation !== undefined && input.relation !== "custom" && input.reference !== undefined && input.support !== undefined) {
      if (plan.spatialDecisions.length >= 16) {
        throw new Error(`O plano já tem 16 decisões espaciais; emende uma existente (${registered}).`);
      }
      const created: TaskSpatialDecision = {
        subject: input.subject,
        ...(input.subjectElementId !== undefined ? { subjectElementId: input.subjectElementId } : {}),
        relation: input.relation,
        reference: input.reference,
        ...(input.referenceElementId !== undefined ? { referenceElementId: input.referenceElementId } : {}),
        support: input.support,
        clearanceMm: input.clearanceMm ?? null,
        allowContact: input.allowContact ?? false,
        ...(input.bottomHeightMm !== undefined && input.bottomHeightMm !== null ? { bottomHeightMm: input.bottomHeightMm } : {}),
        ...(input.topHeightMm !== undefined && input.topHeightMm !== null ? { topHeightMm: input.topHeightMm } : {}),
        ...(input.datum !== undefined && input.datum !== null ? { datum: input.datum } : {}),
        source: "skill",
        rationale: `${input.reason} [decisão registrada durante a execução: ${input.evidence.join("; ")}]`.slice(0, 360),
      } as TaskSpatialDecision;
      const planWithCreated = taskPlanSchema.parse({
        ...plan,
        spatialDecisions: [...plan.spatialDecisions, created],
      });
      const decision = planWithCreated.spatialDecisions[planWithCreated.spatialDecisions.length - 1]!;
      return { plan: planWithCreated, previous: decision, amended: decision, changedFields: ["decision-registered"] };
    }
    throw new Error(`Nenhuma decisão espacial do plano corresponde a “${input.subject}”. As registradas são: ${registered || "nenhuma"}. Emende uma delas pelo subject exato (ou decisionIndex) — ou, para registrar a decisão que falta para este elemento, reenvie a emenda completa com relation, reference e support: ela será adicionada ao plano.`);
  }
  // The same subject may legitimately carry more than one decision (above +
  // aligned). A gate must always have an exit the model can walk: narrow by
  // the relation and the reference the amendment itself names, and when it is
  // still ambiguous, enumerate the candidates with their decisionIndex —
  // "use o subject exato" was a dead end when the subject WAS exact
  // (20/08 12:16 — two invalid amendments and a technical stop).
  if (matches.length > 1 && input.relation !== undefined) {
    const byRelation = matches.filter((decision) => decision.relation === input.relation);
    if (byRelation.length >= 1) matches = byRelation;
  }
  if (matches.length > 1 && (input.referenceElementId !== undefined || input.reference !== undefined)) {
    const byReference = matches.filter((decision) => (
      (input.referenceElementId !== undefined && decision.referenceElementId === input.referenceElementId)
      || (input.reference !== undefined && normalizeSubject(decision.reference) === normalizeSubject(input.reference))
    ));
    if (byReference.length >= 1) matches = byReference;
  }
  if (matches.length > 1) {
    const listing = matches
      .map((decision) => `decisionIndex=${plan.spatialDecisions.indexOf(decision) + 1}: “${decision.subject}” ${decision.relation} → ${decision.reference}`)
      .join("; ");
    throw new Error(`“${input.subject}” corresponde a ${matches.length} decisões espaciais (${listing}). Reenvie a mesma emenda escolhendo a decisão com decisionIndex.`);
  }
  const previous = matches[0]!;
  if (input.relation === "custom") {
    throw new Error("“custom” não é uma relação executável para uma emenda: mantenha a relação da decisão e ajuste folga, suporte, cota (bottomHeightMm/topHeightMm com datum) ou referência.");
  }
  const amended: TaskSpatialDecision = {
    ...previous,
    ...(input.subjectElementId !== undefined ? { subjectElementId: input.subjectElementId } : {}),
    ...(input.reference !== undefined ? { reference: input.reference } : {}),
    ...(input.referenceElementId !== undefined ? { referenceElementId: input.referenceElementId } : {}),
    ...(input.relation !== undefined ? { relation: input.relation } : {}),
    ...(input.support !== undefined ? { support: input.support } : {}),
    ...(input.clearanceMm !== undefined ? { clearanceMm: input.clearanceMm } : {}),
    ...(input.allowContact !== undefined ? { allowContact: input.allowContact } : {}),
    ...(input.bottomHeightMm !== undefined ? { bottomHeightMm: input.bottomHeightMm } : {}),
    ...(input.topHeightMm !== undefined ? { topHeightMm: input.topHeightMm } : {}),
    ...(input.datum !== undefined ? { datum: input.datum } : {}),
    rationale: `${input.reason} [emenda: ${input.evidence.join("; ")}]`.slice(0, 360),
  };
  const changedFields = (["reference", "referenceElementId", "relation", "support", "clearanceMm", "allowContact", "bottomHeightMm", "topHeightMm", "datum"] as const)
    .filter((field) => JSON.stringify(previous[field]) !== JSON.stringify(amended[field]));
  if (!changedFields.length) {
    throw new Error("A emenda não altera nenhum campo: a decisão já está exatamente nos valores enviados. Se um laudo continua objetando, o alvo é outro — registre a decisão que falta para o elemento objetado (emenda completa com relation/reference/support) ou responda ao laudo com a justificativa, em vez de reenviar esta emenda.");
  }
  const parsedDecision = taskSpatialDecisionSchema.parse(amended);
  return {
    plan: taskPlanSchema.parse({
      ...plan,
      revision: Math.min(100, plan.revision + 1),
      spatialDecisions: plan.spatialDecisions.map((decision) => (decision === previous ? parsedDecision : decision)),
    }),
    previous,
    amended: parsedDecision,
    changedFields,
  };
}

export const orchestrationPhaseSchema = z.enum(["direct", "discovery", "planning", "execution", "verification"]);

export const taskExecutionArtifactSchema = z.object({
  stepId: z.string().trim().min(1).max(80),
  instanceKey: z.string().trim().min(1).max(120),
  elementId: z.string().trim().min(1).max(200),
  catalogItemId: z.string().trim().min(1).max(200),
  name: z.string().trim().min(1).max(240),
  status: z.enum(["live", "deleted"]),
  translationMm: z.tuple([z.number(), z.number(), z.number()]).optional(),
});

export const agentOrchestrationContextSchema = z.object({
  phase: orchestrationPhaseSchema,
  objective: z.string().trim().min(1).max(2_000),
  designFreedom: taskDesignFreedomSchema,
  plan: taskPlanSchema.nullable().default(null),
  executionLedger: z.array(taskExecutionArtifactSchema).max(80).default([])
    .describe("Registro durável das instâncias criadas nesta tarefa. Itens live devem ser reutilizados e corrigidos, não recriados."),
  planReview: z.object({
    originalObjective: z.string().trim().min(1).max(2_000),
    designFreedom: taskDesignFreedomSchema,
    environmentSummary: z.string().max(6_000),
    decisions: z.array(z.object({
      subject: z.string().max(160),
      relation: z.string().max(40),
      reference: z.string().max(160),
      support: z.string().max(40),
      bottomHeightMm: z.number().nullable().optional(),
      topHeightMm: z.number().nullable().optional(),
      clearanceMm: z.object({ minimum: z.number(), target: z.number(), maximum: z.number() }).nullable().optional(),
      datum: z.object({ kind: z.string().max(40), reference: z.string().max(160).optional(), justification: z.string().max(500).optional() }).nullable().optional(),
      rationale: z.string().max(600),
      referenceIdentity: z.string().max(300).optional()
        .describe("Quem é a referência na cena: nome, tipologia, posição e o que a árvore de componentes registra (portas, gavetas…). É contra o descritor do pedido que isto se confere."),
      subjectIdentity: z.string().max(300).optional()
        .describe("Quem é o sujeito quando já existe na cena (subjectElementId): nome, tipologia, posição e árvore de componentes."),
    })).max(16).default([]),
    resolutions: z.array(z.object({
      subject: z.string().max(160),
      attribute: z.string().max(40),
      decision: z.string().max(500),
      source: z.string().max(40),
    })).max(24).default([]),
    assumptions: z.array(z.object({
      topic: z.string().max(40),
      assumption: z.string().max(300),
      subject: z.string().max(160).optional(),
      surfaceActivity: z.string().max(40).optional(),
    })).max(12).default([]),
    heightCandidates: z.array(z.object({
      subject: z.string().max(160),
      candidates: z.array(z.string().max(400)).max(10).default([]),
    })).max(8).default([]),
    pendingObjections: z.array(z.string().max(400)).max(6).default([])
      .describe("Objeções do validador que o plano carrega sem resolver (aceite por disjuntor). Se alguma procede, ela é a sua objeção principal."),
    omitted: z.array(z.string().max(200)).max(4).default([])
      .describe("O que o brief cortou por tamanho (contagens explícitas). O plano é maior do que o mostrado; objeção pela amostra só com âncora concreta."),
  }).optional()
    .describe("Brief sanitizado da revisão de prancheta: as decisões do plano com suas razões, o resumo das leituras do ambiente e os candidatos de altura — sem o histórico do planejador."),
  independentReview: z.object({
    originalObjective: z.string().trim().min(1).max(2_000),
    reviewStage: z.enum(["local_step", "final_environment"]).default("final_environment"),
    activeStepId: z.string().trim().min(1).max(80).optional(),
    currentDeliverable: z.string().trim().min(1).max(500).optional(),
    deferredDeliverables: z.array(z.string().trim().min(1).max(300)).max(20).default([]),
    scope: z.string().trim().min(1).max(500),
    hardConstraints: z.array(z.string().trim().min(1).max(300)).max(24).default([]),
    explicitRequirements: z.array(z.object({
      subject: z.string().trim().min(1).max(160),
      attribute: taskDesignAttributeSchema,
      requirement: z.string().trim().min(1).max(360),
    })).max(40).default([]),
    spatialIntents: z.array(z.object({
      subject: z.string().trim().min(1).max(160),
      relation: taskSpatialDecisionSchema.shape.relation,
      reference: z.string().trim().min(1).max(160),
      support: taskSpatialDecisionSchema.shape.support,
    })).max(16).default([]),
    changedElements: z.array(z.object({
      elementId: z.string().trim().min(1).max(200),
      name: z.string().trim().min(1).max(240),
      agentOwnedAttributes: z.array(taskDesignAttributeSchema).max(9).default([]),
      requiredAspects: z.array(taskDesignReviewCriterionSchema.shape.aspect).max(16).default([]),
    })).max(80).default([]),
    acceptanceCriteria: z.array(z.string().trim().min(1).max(300)).max(40).default([]),
    reviewHistory: z.array(z.object({
      elementName: z.string(),
      round: z.number().int().min(1),
      verdict: z.enum(["approved", "rejected", "unverifiable"]),
      failedAspects: z.array(z.string()).max(16).default([]),
      corrections: z.array(z.string()).max(4).default([]),
      summary: z.string().optional(),
      stateThen: z.object({ bottomMm: z.number(), topMm: z.number() }).nullable().optional(),
      stateNow: z.object({ bottomMm: z.number(), topMm: z.number() }).nullable().optional(),
    })).max(24).optional()
      .describe("Os laudos anteriores DESTE crítico nesta rodada sobre os mesmos alvos (aspectos reprovados, correções pedidas, cota então e agora). Coerência obrigatória: não reabra com a objeção oposta o que foi corrigido como pedido."),
    environmentContext: z.object({
      roomFunction: z.object({
        value: z.string(),
        confidence: z.string(),
        evidence: z.array(z.string()).max(8).default([]),
      }),
      ceilingHeightMm: z.number(),
      userProfile: z.object({
        label: z.string(),
        reach: z.object({
          primeMm: z.tuple([z.number(), z.number()]),
          acceptableMm: z.tuple([z.number(), z.number()]),
          headZoneMm: z.tuple([z.number(), z.number()]),
          storageTopMaxMm: z.number(),
        }),
      }),
      walls: z.array(z.object({
        wallId: z.string(),
        lines: z.array(z.object({
          heightMm: z.number(),
          kind: z.string(),
          family: z.string(),
          sourceName: z.string().optional(),
        })).max(24).default([]),
      })).max(16).default([]),
      elements: z.array(z.object({
        name: z.string(),
        typology: z.string(),
        suspended: z.boolean(),
        bottomMm: z.number(),
        topMm: z.number(),
        wallId: z.string().nullable(),
      })).max(60).default([]),
      surfaces: z.array(z.object({
        name: z.string(),
        topMm: z.number(),
        activity: z.string(),
        confidence: z.string(),
      })).max(40).default([]),
      assumptions: z.array(taskAssumptionSchema).max(8).default([]),
      datumDecisions: z.array(z.object({
        subject: z.string(),
        kind: taskVerticalDatumSchema.shape.kind,
        reference: z.string().optional(),
        justification: z.string().optional(),
      })).max(16).default([])
        .describe("Linhas de referência declaradas no plano para cotas verticais (sem os números): o crítico confere se a intenção foi cumprida e se é profissional; não a reescreve."),
    }).optional().describe("Leitura do ambiente produzida pelo sistema (linhas das paredes, função do cômodo, superfícies, alcance) e premissas declaradas no plano: contexto de comparação, não a solução escolhida."),
  }).optional().describe("Briefing sanitizado para um crítico visual sem acesso à solução escolhida pelo planejador."),
});

export type TaskComplexity = z.infer<typeof taskComplexitySchema>;
export type TaskDesignFreedom = z.infer<typeof taskDesignFreedomSchema>;
export type TaskPlanProposal = z.infer<typeof taskPlanProposalSchema>;
export type TaskSpatialDecision = z.infer<typeof taskSpatialDecisionSchema>;
export type TaskVerticalDatum = z.infer<typeof taskVerticalDatumSchema>;
export type TaskAssumption = z.infer<typeof taskAssumptionSchema>;
export type TaskDecisionAuthority = z.infer<typeof taskDecisionAuthoritySchema>;
export type TaskDesignResolution = z.infer<typeof taskDesignResolutionSchema>;
export type TaskDesignReviewCriterion = z.infer<typeof taskDesignReviewCriterionSchema>;
export type TaskPlanProgressInput = z.infer<typeof taskPlanProgressInputSchema>;
export type TaskPlan = z.infer<typeof taskPlanSchema>;
export type OrchestrationPhase = z.infer<typeof orchestrationPhaseSchema>;
export type TaskExecutionArtifact = z.infer<typeof taskExecutionArtifactSchema>;

export function createTaskPlan(proposal: TaskPlanProposal, id: string, revision = 1): TaskPlan {
  return taskPlanSchema.parse({
    ...proposal,
    id,
    revision,
    steps: proposal.steps.map((step) => ({
      ...step,
      status: "pending",
      confirmedEvidence: [],
    })),
  });
}

export function updateTaskPlan(plan: TaskPlan, input: TaskPlanProgressInput): TaskPlan {
  const parsed = taskPlanProgressInputSchema.parse(input);
  if (!plan.steps.some((step) => step.id === parsed.stepId)) {
    throw new Error(`Etapa desconhecida no plano: ${parsed.stepId}.`);
  }
  return taskPlanSchema.parse({
    ...plan,
    steps: plan.steps.map((step) => step.id === parsed.stepId
      ? {
          ...step,
          status: parsed.status,
          note: parsed.note,
          confirmedEvidence: parsed.evidence,
        }
      : step),
  });
}

export function taskPlanNeedsApproval(plan: TaskPlan): boolean {
  // A complexidade muda a profundidade do plano, não transfere ao usuário a
  // responsabilidade de decidir como executá-lo. Confirmações destrutivas e
  // impedimentos materiais continuam protegidos por políticas próprias.
  void plan;
  return false;
}

export function taskPlanIsComplete(plan: TaskPlan): boolean {
  return plan.steps.every((step) => step.status === "completed");
}

export function taskPlanProgress(plan: TaskPlan): { completed: number; total: number; current: string | null } {
  const completed = plan.steps.filter((step) => step.status === "completed").length;
  const current = plan.steps.find((step) => step.status === "in_progress")
    ?? plan.steps.find((step) => step.status === "pending")
    ?? plan.steps.find((step) => step.status === "blocked")
    ?? null;
  return { completed, total: plan.steps.length, current: current?.title ?? null };
}

/**
 * A single human message produces a single action packet and, when planning is
 * needed, exactly one plan. This structural check prevents a valid-looking
 * plan from silently covering only the first action in a mixed or multi-action
 * turn.
 */
export function taskPlanActionCoverageIssues(
  proposal: TaskPlanProposal,
  requestedActionIds: readonly string[],
): string[] {
  if (!requestedActionIds.length) return [];
  const requested = new Set(requestedActionIds);
  const stepIds = new Set(proposal.steps.map((step) => step.id));
  const coverage = proposal.actionCoverage ?? [];
  const issues: string[] = [];

  for (const actionId of requested) {
    const entries = coverage.filter((entry) => entry.actionId === actionId);
    if (!entries.length) {
      issues.push(`A ação ${actionId} não foi vinculada a nenhuma etapa deste plano.`);
      continue;
    }
    const unknownSteps = entries.flatMap((entry) => entry.stepIds).filter((stepId) => !stepIds.has(stepId));
    if (unknownSteps.length) {
      issues.push(`A ação ${actionId} aponta para etapas inexistentes: ${[...new Set(unknownSteps)].join(", ")}.`);
    }
  }
  const unrelated = coverage.filter((entry) => !requested.has(entry.actionId));
  if (unrelated.length) {
    issues.push(`O plano declarou ações que não pertencem ao pacote atual: ${[...new Set(unrelated.map((entry) => entry.actionId))].join(", ")}.`);
  }
  return issues;
}

const STRICT_REFERENCE_PATTERN = /\b(?:faca|fazer|modele|monte|reproduza|copie|siga|execute)\b.{0,80}\b(?:igual|identic[oa]|fiel(?:mente)?|exatamente|conforme|de acordo com)\b|\b(?:igual|identic[oa]|fiel(?:mente)?|exatamente|conforme)\b.{0,80}\b(?:anexo|arquivo|desenho|projeto|imagem|foto|planta|croqui|referencia)|\b(?:reproduza|copie)\b.{0,80}\b(?:anexo|arquivo|desenho|projeto|imagem|foto|planta|croqui|referencia)/;
const INSPIRATION_PATTERN = /\b(?:inspir\w*|referencia visual|parecido|semelhante|linguagem|estilo)\b/;
const OPEN_DESIGN_PATTERN = /\b(?:crie|faca|modele|monte|projete|planeje|componha|organize|decore|harmonize|desenhe|desenvolva|complete|repense|melhore|transforme)\b|\b(?:decida|escolha)\b.{0,50}\b(?:melhor|tudo|implantacao|layout|composicao|medidas?|posicao)|\b(?:solucao criativa|conceito|autoria|liberdade criativa)\b/;
const DELEGATED_DESIGN_PATTERN = /\b(?:decida|escolha|melhor|bonit\w*|agradavel|harmon\w*|coerente|adequad[oa]s?|profissional|bem resolvid[oa])\b/;
const NARROW_EXACT_ACTION_PATTERN = /\b(?:mude|altere|ajuste|redimensione|gire|mova|pinte|aplique|troque|renomeie|selecione|abra|feche|apague|remova)\b/;
const EXPLICIT_VALUE_PATTERN = /\d|\b(?:mm|cm|m|graus?|branco|preto|cinza|bege|azul|verde|vermelho|amarelo)\b/;
const STATE_ONLY_ACTION_PATTERN = /\b(?:selecione|abra|feche|apague|remova|renomeie)\b/;
const VISUAL_DESIGN_ACTION_PATTERN = /\b(?:adicione|adiciona|adicionar|acrescente|acrescenta|acrescentar|insira|insere|inserir|inclua|inclui|incluir|coloque|coloca|colocar|bote|bota|botar|ponha|poe|crie|cria|criar|instale|instala|instalar|implante|implanta|implantar|mova|move|mover|reposicione|reposiciona|reposicionar|ajuste|ajusta|ajustar|redimensione|redimensiona|redimensionar|gire|gira|girar|pinte|pinta|pintar|aplique|aplica|aplicar|troque|troca|trocar|modele|modela|modelar|monte|monta|montar|projete|projeta|projetar|planeje|planeja|planejar|componha|compoe|compor|organize|organiza|organizar|decore|decora|decorar|harmonize|harmoniza|harmonizar|desenhe|desenha|desenhar|desenvolva|desenvolve|desenvolver|complete|completa|completar|repense|repensa|repensar|melhore|melhora|melhorar|transforme|transforma|transformar)\b/;
const EXPLICIT_TRANSFORM_VALUE_PATTERN = /\d|\b(?:mm|cm|metros?|graus?|branco|preto|cinza|bege|azul|verde|vermelho|amarelo)\b/;

/**
 * Design freedom is independent from orchestration complexity. A whole project
 * can be strict when it comes from a dimensioned drawing, while one small
 * insertion can need professional authorship when its size and placement are
 * left open.
 */
export function inferTaskDesignFreedom(request: string, hasAttachments = false): TaskDesignFreedom {
  const normalized = normalizeRequest(request);
  if (!normalized) return hasAttachments ? "guided" : "strict";
  if (STRICT_REFERENCE_PATTERN.test(normalized)) return "strict";
  if (INSPIRATION_PATTERN.test(normalized)) return "guided";
  if (OPEN_DESIGN_PATTERN.test(normalized) || DELEGATED_DESIGN_PATTERN.test(normalized)) {
    return EXPLICIT_VALUE_PATTERN.test(normalized) || hasAttachments ? "guided" : "open";
  }
  if (NARROW_EXACT_ACTION_PATTERN.test(normalized) && EXPLICIT_VALUE_PATTERN.test(normalized)) return "strict";
  return "guided";
}

/** A plan may narrow the inferred freedom after discovery, but never broaden it. */
export function restrictTaskDesignFreedom(
  inferred: TaskDesignFreedom,
  proposed: TaskDesignFreedom,
): TaskDesignFreedom {
  const rank: Record<TaskDesignFreedom, number> = { strict: 0, guided: 1, open: 2 };
  return rank[proposed] <= rank[inferred] ? proposed : inferred;
}

/**
 * Only obvious, narrow operations bypass discovery. Ambiguous spatial language,
 * multiple deliveries and creative verbs deliberately go through a plan.
 */
export function taskNeedsPlanning(request: string, hasAttachments = false): boolean {
  const normalized = normalizeRequest(request);
  if (hasAttachments) return true;
  if (!normalized) return false;
  if (/^(?:qual|quais|quanto|quantos|onde|como|por que|porque|o que|me diga|explique|confira|verifique|leia)\b/.test(normalized)) {
    return false;
  }
  if (/\b(?:crie|projete|planeje|componha|organize|decore|harmonize|desenhe|monte|repense|melhore)\b/.test(normalized)
    || DELEGATED_DESIGN_PATTERN.test(normalized)) {
    return true;
  }
  const multiPart = /[;\n]|\b(?:depois|tambem|alem disso|em seguida|e entao)\b/.test(normalized);
  const exactAction = /\b(?:mude|altere|ajuste|redimensione|gire|mova|pinte|aplique|troque|renomeie|selecione|abra|feche|apague|remova)\b/.test(normalized);
  const stateOnly = STATE_ONLY_ACTION_PATTERN.test(normalized)
    && !/\b(?:mova|ajuste|redimensione|gire|pinte|aplique|troque)\b/.test(normalized);
  if (normalized.length <= 240 && !multiPart && stateOnly) return false;
  const exactTransform = EXPLICIT_TRANSFORM_VALUE_PATTERN.test(normalized);
  return !(normalized.length <= 240 && !multiPart && exactAction && exactTransform);
}

/**
 * A visual method is mandatory only for mutating work that actually delegates
 * judgment. Exact reproduction and fully specified edits remain faithful and
 * do not acquire aesthetic freedom merely because a skill exists.
 */
export function taskRequiresVisualComposition(request: string, hasAttachments = false): boolean {
  const normalized = normalizeRequest(request);
  if (!normalized || !VISUAL_DESIGN_ACTION_PATTERN.test(normalized)) return false;
  return taskNeedsPlanning(request, hasAttachments)
    && inferTaskDesignFreedom(request, hasAttachments) !== "strict";
}

/** A new, unspecified physical item needs real catalog evidence before design is frozen. */
export function taskRequiresCatalogDiscovery(request: string): boolean {
  const normalized = normalizeRequest(request);
  const creation = /\b(?:adicione|adiciona|adicionar|acrescente|acrescenta|acrescentar|insira|insere|inserir|inclua|inclui|incluir|coloque|coloca|colocar|bote|bota|botar|ponha|poe|crie|cria|criar|instale|instala|instalar|implante|implanta|implantar)\b/g;
  for (const match of normalized.matchAll(creation)) {
    const following = normalized.slice((match.index ?? 0) + match[0].length, (match.index ?? 0) + match[0].length + 90);
    if (/\b(?:um|uma|algum|alguma|novo|nova|novos|novas)\b/.test(following)) return true;
  }
  return false;
}

function normalizeRequest(request: string): string {
  return request.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").trim();
}
